import React, { useState, Suspense, lazy } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import AuthContainer from './components/auth/AuthContainer';
import Sidebar, { PageId } from './components/layout/Sidebar';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import './components/admin/AdminStyles.css';

const Dashboard = lazy(() => import('./components/Dashboard'));
const OperationsList = lazy(() => import('./components/OperationsList'));
const UserConfig = lazy(() => import('./components/UserConfig'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const CsvImport = lazy(() => import('./components/CsvImport'));
const AutoCategorization = lazy(() => import('./components/AutoCategorization'));
const Keywords = lazy(() => import('./components/Keywords'));
const BankAccountManager = lazy(() => import('./components/BankAccountManager'));
const CategoriesManagement = lazy(() => import('./components/CategoriesManagement'));
const BudgetManager = lazy(() => import('./components/BudgetManager'));
const TagManagement = lazy(() => import('./components/TagManagement'));
const WorkflowsPage = lazy(() => import('./components/workflows/WorkflowsPage'));
const WorkflowRun = lazy(() => import('./components/workflows/WorkflowRun'));
const AuditLogs = lazy(() => import('./components/AuditLogs'));

const Fallback: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 40,
      color: 'var(--text-muted)',
      fontSize: 13,
    }}
  >
    <div className="loading-spinner" />
  </div>
);

const AppContent: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<PageId>('dashboard');
  // Exécution de workflow à afficher sur la page 'workflow-run'
  const [runId, setRunId] = useState<number | null>(null);
  const { isAuthenticated, isLoading } = useAuth();
  const { sidebarCollapsed, toggleSidebar } = useTheme();

  if (isLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AuthContainer />;
  }

  const handleNavigate = (page: string) => {
    setCurrentPage(page as PageId);
  };

  return (
    <div className="app-shell">
      <Sidebar
        currentPage={currentPage}
        onNavigate={(p) => setCurrentPage(p)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
      />
      <div className="app-main">
        <ErrorBoundary key={currentPage}>
        <Suspense fallback={<Fallback />}>
          {currentPage === 'dashboard' && <Dashboard onNavigate={handleNavigate} />}
          {currentPage === 'operations' && <OperationsList />}
          {currentPage === 'accounts' && <BankAccountManager />}
          {currentPage === 'budgets' && <BudgetManager />}
          {currentPage === 'tags' && <TagManagement />}
          {currentPage === 'config' && <UserConfig />}
          {currentPage === 'admin' && <AdminDashboard />}
          {currentPage === 'audit-logs' && <AuditLogs />}
          {currentPage === 'categories' && <CategoriesManagement />}
          {currentPage === 'auto-categorization' && <AutoCategorization />}
          {currentPage === 'keywords' && <Keywords />}
          {currentPage === 'import' && (
            <CsvImport
              onNavigate={handleNavigate}
              onStartRun={(executionId) => {
                setRunId(executionId);
                setCurrentPage('workflow-run');
              }}
            />
          )}
          {currentPage === 'workflows' && (
            <WorkflowsPage
              onResume={(executionId) => {
                setRunId(executionId);
                setCurrentPage('workflow-run');
              }}
            />
          )}
          {currentPage === 'workflow-run' && runId !== null && (
            <WorkflowRun executionId={runId} onNavigate={(p) => setCurrentPage(p)} />
          )}
        </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <AuthProvider>
      <PermissionProvider>
        <AppContent />
      </PermissionProvider>
    </AuthProvider>
  </ThemeProvider>
);

export default App;
